import type { SpriteDefinition } from '@/render/spriteBaker';
import { PALETTE } from '@/sprites/palette';

/**
 * Katie, 16x16, three facings with three frames each.
 *
 * `left` is `right` mirrored at draw time rather than duplicated here — half
 * the art to keep in sync, and identical silhouettes by construction.
 */

export type Facing = 'down' | 'up' | 'left' | 'right';
export type KatieFrame = 'idle' | 'walkA' | 'walkB';

const DOWN_HEAD = [
  '................',
  '.....hhhhhh.....',
  '....hhhhhhhh....',
  '....hkkkkkkh....',
  '....hssssssh....',
  '....hsessesh....',
  '....hssSSssh....',
  '.....ssssss.....',
];

const DOWN_BODY = [
  '...dddwwwwddd...',
  '...sddwwwwdds...',
  '...sddwwwwdds...',
  '...dddwwwwddd...',
  '..DDddwwwwddDD..',
];

const UP_HEAD = [
  '................',
  '.....hhhhhh.....',
  '....hhhhhhhh....',
  '....hhhhhhhh....',
  '....hhhhhhhh....',
  '....hhkkkkhh....',
  '....hhhhhhhh....',
  '.....hhhhhh.....',
];

const UP_BODY = [
  '...dddddddddd...',
  '...sdddddddds...',
  '...sdddddddds...',
  '...dddddddddd...',
  '..DDddddddddDD..',
];

const SIDE_HEAD = [
  '................',
  '......hhhh......',
  '.....hhhhhh.....',
  '....hhhhhhh.....',
  '....hkssssh.....',
  '....hssesss.....',
  '....hsssssS.....',
  '.....sssss......',
];

const SIDE_BODY = [
  '....ddwwwwd.....',
  '....sdwwwwd.....',
  '....sdwwwwd.....',
  '....ddwwwwd.....',
  '...DDdwwwwdD....',
];

/** Front/back legs. Three poses read as a walk cycle at ~8fps. */
const LEGS_FRONT: Record<KatieFrame, string[]> = {
  idle: ['.....ss..ss.....', '.....ss..ss.....', '.....bb..bb.....'],
  walkA: ['....ss....ss....', '....ss....ss....', '...bb......bb...'],
  walkB: ['......ssss......', '......ssss......', '......bbbb......'],
};

const LEGS_SIDE: Record<KatieFrame, string[]> = {
  idle: ['.....ss.ss......', '.....ss.ss......', '.....bb.bb......'],
  walkA: ['....ss...ss.....', '....ss...ss.....', '...bb.....bb....'],
  walkB: ['......ssss......', '......ssss......', '......bbbb......'],
};

function build(name: string, head: string[], body: string[], legs: string[]): SpriteDefinition {
  return { name, rows: [...head, ...body, ...legs], palette: PALETTE };
}

export const KATIE_SPRITES = {
  'down-idle': build('katie-down-idle', DOWN_HEAD, DOWN_BODY, LEGS_FRONT.idle),
  'down-walkA': build('katie-down-walkA', DOWN_HEAD, DOWN_BODY, LEGS_FRONT.walkA),
  'down-walkB': build('katie-down-walkB', DOWN_HEAD, DOWN_BODY, LEGS_FRONT.walkB),
  'up-idle': build('katie-up-idle', UP_HEAD, UP_BODY, LEGS_FRONT.idle),
  'up-walkA': build('katie-up-walkA', UP_HEAD, UP_BODY, LEGS_FRONT.walkA),
  'up-walkB': build('katie-up-walkB', UP_HEAD, UP_BODY, LEGS_FRONT.walkB),
  'right-idle': build('katie-right-idle', SIDE_HEAD, SIDE_BODY, LEGS_SIDE.idle),
  'right-walkA': build('katie-right-walkA', SIDE_HEAD, SIDE_BODY, LEGS_SIDE.walkA),
  'right-walkB': build('katie-right-walkB', SIDE_HEAD, SIDE_BODY, LEGS_SIDE.walkB),
} as const satisfies Record<string, SpriteDefinition>;

export type KatieSpriteKey = keyof typeof KATIE_SPRITES;

/** Left reuses the right-facing textures with a horizontal flip. */
export function spriteKeyFor(facing: Facing, frame: KatieFrame): KatieSpriteKey {
  const direction = facing === 'left' ? 'right' : facing;
  return `${direction}-${frame}`;
}
